import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api, AUTH_REFRESH_TOKEN_STORAGE_KEY } from './api'

function assertSuccess(body) {
  if (!body?.success) {
    const err = new Error(sanitizeBusinessMessage(body?.message, 'Authentication failed'))
    err.code = body?.error?.code
    throw err
  }
  return body.data
}

function apiError(err: unknown, opts?: { loginAttempt?: boolean }) {
  return toUserFacingApiError(err, opts?.loginAttempt ? 'Login failed' : 'Request failed', {
    loginAttempt: opts?.loginAttempt,
  })
}

export type SupportSessionInfo = {
  sessionId: string
  accessMode: 'read_only' | 'full'
  organisationId: number
  organisationName: string
  superAdminId: number
  /** Unix ms when session expires (client-side countdown). */
  expiresAtMs: number
}

export type AuthUser = {
  id: string
  accountId?: string
  email: string
  name: string
  role: string
  organisationId: number | null | undefined
  mustChangePassword: boolean
  billingRequired?: boolean
  modules?: string[]
  organisationName?: string
  organisation?: { id: number; name: string }
  supportSession?: SupportSessionInfo
}

export type AccessibleStore = {
  storeId: number
  name: string
  subdomain: string
  organisationId: number
  role: 'store_owner' | 'staff'
  modules: string[]
  adminBlocked: boolean
  billingRequired: boolean
  subscription?: {
    planCode: string
    status: string
    billingRequired: boolean
    complimentary?: boolean
    displayPrice?: string | null
  } | null
}

export const AUTH_STORES_STORAGE_KEY = 'shopora_store_admin_stores'
export const CURRENT_STORE_STORAGE_KEY = 'shopora_store_admin_current_store_id'

/**
 * POST /auth/login — returns JWT for super_admin or tenant users.
 */
export async function login({ email, password }): Promise<{
  user: AuthUser
  token: string
  refreshToken?: string
  stores?: AccessibleStore[]
}> {
  try {
    const { data } = await api.post('/auth/login', {
      email: email?.trim(),
      password,
    })
    const payload = assertSuccess(data) as {
      user: Record<string, unknown>
      account?: { id: number }
      stores?: AccessibleStore[]
      accessToken: string
      refreshToken?: string
      organisation?: { id: number; name: string; status?: string }
    }
    const u = payload.user
    const org = payload.organisation
    const user: AuthUser = {
      id: String(u.id),
      ...(payload.account?.id != null ? { accountId: String(payload.account.id) } : {}),
      email: String(u.email),
      name: String(u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email),
      role: String(u.role),
      organisationId: u.organisationId as number | null | undefined,
      mustChangePassword: Boolean(u.mustChangePassword),
      billingRequired: Boolean(u.billingRequired),
      ...(Array.isArray(u.modules) ? { modules: u.modules.map(String) } : {}),
      ...(org
        ? {
            organisationName: org.name,
            organisation: { id: org.id, name: org.name },
          }
        : {}),
    }
    const stores = Array.isArray(payload.stores) ? payload.stores : undefined
    if (stores) {
      localStorage.setItem(AUTH_STORES_STORAGE_KEY, JSON.stringify(stores))
    } else {
      localStorage.removeItem(AUTH_STORES_STORAGE_KEY)
    }
    return {
      user,
      token: payload.accessToken,
      refreshToken: typeof payload.refreshToken === 'string' ? payload.refreshToken : undefined,
      stores,
    }
  } catch (e) {
    throw apiError(e, { loginAttempt: true })
  }
}

export async function changePassword({ currentPassword, newPassword }) {
  try {
    const { data } = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    })
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e)
  }
}

export async function requestPasswordReset(email: string) {
  try {
    const { data } = await api.post('/auth/forgot-password', { email: email?.trim() })
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e)
  }
}

export async function resetPassword({
  email,
  code,
  newPassword,
}: {
  email: string
  code: string
  newPassword: string
}) {
  try {
    const { data } = await api.post('/auth/reset-password', {
      email: email?.trim(),
      code: code?.trim(),
      newPassword,
    })
    return assertSuccess(data)
  } catch (e) {
    throw apiError(e)
  }
}

export async function fetchAccessibleStores(): Promise<AccessibleStore[]> {
  try {
    const { data } = await api.get('/storebusiness/me/stores')
    const payload = assertSuccess(data) as { stores?: AccessibleStore[] }
    const stores = Array.isArray(payload.stores) ? payload.stores : []
    localStorage.setItem(AUTH_STORES_STORAGE_KEY, JSON.stringify(stores))
    return stores
  } catch (e) {
    throw apiError(e)
  }
}

export async function logout(refreshTokenOverride?: string | null) {
  const refresh =
    refreshTokenOverride !== undefined
      ? refreshTokenOverride
      : localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
  if (refresh) {
    try {
      await api.post('/auth/logout', { refreshToken: refresh }, { skipAuthRefresh: true })
    } catch {
      // best-effort revoke
    }
  }
  localStorage.removeItem(AUTH_STORES_STORAGE_KEY)
  localStorage.removeItem(CURRENT_STORE_STORAGE_KEY)
}

/**
 * POST /auth/support-access/exchange — one-time launch token → short-lived org-scoped JWT.
 */
export async function exchangeSupportAccess(token: string): Promise<{
  user: AuthUser
  token: string
}> {
  try {
    const { data } = await api.post(
      '/auth/support-access/exchange',
      { token: token?.trim() },
      { skipAuthRefresh: true },
    )
    const payload = assertSuccess(data) as {
      accessToken: string
      user: Record<string, unknown>
      organisation?: { id: number; name: string; status?: string }
    }
    const u = payload.user
    const org = payload.organisation
    const rawSession = u.supportSession as Record<string, unknown> | undefined
    const expiresInSeconds =
      typeof rawSession?.expiresInSeconds === 'number' ? rawSession.expiresInSeconds : 3600
    const supportSession: SupportSessionInfo | undefined = rawSession
      ? {
          sessionId: String(rawSession.sessionId ?? ''),
          accessMode: rawSession.accessMode === 'full' ? 'full' : 'read_only',
          organisationId: Number(rawSession.organisationId ?? org?.id ?? u.organisationId),
          organisationName: String(rawSession.organisationName ?? org?.name ?? ''),
          superAdminId: Number(rawSession.superAdminId ?? 0),
          expiresAtMs: Date.now() + expiresInSeconds * 1000,
        }
      : undefined
    const user: AuthUser = {
      id: String(u.id),
      email: String(u.email),
      name: String(u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email),
      role: String(u.role),
      organisationId: u.organisationId as number | null | undefined,
      mustChangePassword: false,
      ...(supportSession ? { supportSession } : {}),
      ...(org
        ? {
            organisationName: org.name,
            organisation: { id: org.id, name: org.name },
          }
        : {}),
    }
    return {
      user,
      token: payload.accessToken,
    }
  } catch (e) {
    throw apiError(e)
  }
}
